import { AgentInfo } from "src/app/angular-elements/shared/interfaces/agent-info.interface";
import { PlanAttributes, SummaryView } from "../../../plan-selection/interfaces/plan-attributes.interface";
import { Product } from "../../../plan-selection/interfaces/product.interface";
import { PAYLOAD_PERSON_TYPES, EXCEL_PERSON_TYPES } from "./mass-upload-grid.interface";

export const SUBMISSION_DATE_FORMAT = 'MM/DD/YYYY';
export const MOMENT_PARSE_FORMAT = 'M/D/YYYY';  // Handles all combinations of formats MM, M, DD, D

export enum CASE_STATUS {
  IN_PROGRESS = 'In Progress',
  SUBMITTED = 'Submitted'
}

export enum SUBMISSION_STATUS {
  PENDING = 'PENDING',
  SUBMITTED = 'SUBMITTED'
}

export interface EnrollmentRequestPayload {
  enrollments: EnrollmentRequest[];
}

export interface EnrollmentResponsePayload {
  caseStatus: CASE_STATUS;
  enrollments: EnrollmentRequest[];
  totalEnrollments: number;
}

export interface EnrollmentSubmissionResponse {
    payload: string;
}

export interface EnrollmentRequest {
  portalTrackingNumber: string | null;
  applicant: MassUploadApplicant;
  agentInfo: AgentInfo,
  submitStatus: string;
  enrollmentType: string;
  planRegion: string;
  hiosId: string;
  ddValue: string;
  ubk: string;
  market: string;
  productType: string;
  productDescription: string;
  benefits: boolean;
  hasOtherHealthPlan: boolean;
  hasOtherHealthPlanReplace: boolean;
  otherHealthPlan?: OtherHealthPlan;
  otherHealthPlanReplace?: OtherHealthPlanReplace;
  thirdPartyPayerType: ThirdPartyPayerType;
  marketingMaterial: boolean;
  deductibleInfo: boolean;
  currentCustomer: boolean;
  retainedSignedCopy: boolean;
  communicationPreferenceIndicator: string;
  effectiveDate: string;
  childOnlyPolicy: boolean;
  producerPhoneNumber: string | null;
  agencyName: string;
  agencyNumber: string;
  producerName: string;
  producerNumber: string;
  planDetails: Product<PlanAttributes, SummaryView>;
  producerInd: boolean; // flags user as doing enrollment from logged in agent perspective
}
export interface EnrollmentRequest_MN_Variation extends EnrollmentRequest {
  mnPermanentResident: boolean;
  producerFullName: SignatureInfo; // MN attestations require a broken out first and last name
}

export interface SignatureInfo {
  firstName: string;
  lastName: string;
}

export interface MassUploadCommonApplicant {
  sameAddressAsSubscriber: boolean;
  firstName: string;
  middleInitial: string | null;
  lastName: string;
  dob: string;
  gender: string;
  tobaccoIndicator: boolean;
  ssn: string;
  isHispanic: string;
  yesToHispanic: string;
  noToHispanic: string;
  relationship: EXCEL_PERSON_TYPES | PAYLOAD_PERSON_TYPES;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  zipCode: string;
  county: string;
  state: string;
  donateLifeRegistry: boolean;
}

export interface MassUploadEnrollmentDependent extends MassUploadCommonApplicant {
  eSignature: string; // This field is likely not being properly utilized. Rather, the eSignatureSpouseFullName
}
export interface MassUploadEnrollmentDependent_MN_Variation extends MassUploadEnrollmentDependent {
  ethnicBackground: EthnicBackgroundOptions; // This field is likely not being properly utilized. Rather, the eSignatureSpouseFullName
}

export interface MassUploadApplicant extends MassUploadCommonApplicant {
  sameMailingAddress: boolean;
  suffix: string;
  email: string;
  specialAssistance: boolean;
  disabledDependent: boolean;

  eSignatureProducer: boolean;

  eSignatureSpouseDomesticFullName: SignatureInfo,
  
  eSignatureFullName: SignatureInfo,
  eSignature: boolean;

  languageSpoken: string;
  languageSpokenCode: string | null;
  languageRead: string;
  languageReadCode: string | null;
  languageWritten: string; // MN-only as of 2026-07-16
  languageWrittenCode: string | null; // MN-only as of 2026-07-16
  mailingAddressLine1: string | null;
  mailingAddressLine2: string | null;
  mailingCity: string | null;
  mailingZipCode: string | null;
  mailingState: string | null;
  homePhoneNumber: string | null;
  workPhoneNumber: string | null;
  mobilePhoneNumber: string | null;
  sameAddressAsSubscriber: boolean | null;
  dependents: MassUploadEnrollmentDependent[];
  disabilityNames: string | null;
};

export interface EthnicBackgroundOptions {
    "B": boolean; // "Black or African American"
    "J": boolean; // "Native Hawaiian/Other Pacific Islander"
    "C": boolean; // "White"
    "A": boolean; // "Asian"
    "G": boolean; // "American Indian or Alaskan Native"
    "E": boolean; // "Other, please specify"
    "otherEthnicBackground": boolean; // "Please Specify Race"
    "CNA": boolean; // "Choose not to answer"
}
export interface MassUploadApplicant_MN_Variation extends MassUploadApplicant {
  mailingAddressExists: boolean;
  isMailingBilling: boolean;
  primaryPhoneNumber: string | null;
  billingAddressLine1: string; // "hyd3",
  billingCity: string; // "MINNEOTA",
  billingState: string; // "MN",
  billingZipCode: string; // "56264",
  ethnicBackground: EthnicBackgroundOptions;
}

/**
 * For use when we want to use the same function to return 2 different types
 */
export type ValidEthnicBackgroundApplicant = MassUploadApplicant_MN_Variation | MassUploadEnrollmentDependent_MN_Variation;

/**
 * Proxy-Signature START
 * 
 * These are not used in any payloads. These interfaces are purely for UI sake.
 * 
 * These proxy interfaces are used thusly:
 * 1) RESPONSE: { applicant: { firstName: "name", eSignatureFullName: { firstName: "not-name" } } }
 * 2) PROBLEM - flattening logic forces applicant.firstName and eSignatureFullName.firstName to conflict
 * 3) SOLUTION: 
 *              The response is manipulated,
 *              Attribute values are copied and renamed
 *                enrollments[0].applicant.eSignatureFullName.firstName -> enrollments[0].applicant.eSignatureFullName.attestationFirstNameCustomer
 *                enrollments[0].producerFullName.firstName -> enrollments[0].applicant.eSignatureProducerFullNameProxy.attestationFirstNameProducer
 *                  Importantly! These new, proxy attributes are required to be unique to avoid the FLATTENING OVERRIDE
 * 
 *  This requires alignment in mass-upload-grid 
 */
export interface ProxiedApplicantAttestationMassUploadApplicant extends MassUploadApplicant {
  eSignatureFullNameProxy?: {
    attestationFirstNameCustomer: string;
    attestationLastNameCustomer: string;
  }
}

export interface ProxiedSpouseAttestationMassUploadApplicant extends MassUploadApplicant {
  attestationFirstNameCustomer: string;
  attestationLastNameCustomer: string;
}


export interface ProxiedApplicantAttestationMassUploadApplicantWithProducerFullName extends MassUploadApplicant_MN_Variation {
  eSignatureProducerFullNameProxy?: {
    attestationFirstNameAgent: string;
    attestationLastNameAgent: string;
  }
}
/**
 * Proxy-Signature END
 */


export interface OtherHealthPlan {
  fullyConsumedBenefitsInd: boolean | null;
  ohpPAndGInsuranceCarrier: string | null;
  ohpPAndGGroupNumber: string | null;
  ohpPAndGPolicyHolder: string | null;
  ohpPAndGEffectiveDate: string | null;
  ohpPAndGPolicyNumber: string | null;
  ohpPAndGRelationToApplicant: string | null;
  ohpPAndGPolicyHolderDob: string | null;
  ohpPAndGEmploymentStatus: string | null;
}

export interface OtherHealthPlanReplace {
  ohpReplaceInsuranceCarrier: string | null;
  ohpReplaceGroupNumber: string | null;
  ohpReplacePolicyHolder: string | null;
  ohpReplaceEffectiveDate: string | null;
  ohpReplacePolicyNumber: string | null;
  ohpReplaceRelationToApplicant: string | null;
  ohpReplacePolicyHolderDob: string | null;
}

export interface ThirdPartyPayerType {
  premiumPayment: boolean;
  payerType: string;
  ichra: boolean;
  qsehra: boolean;
  dateOfLifeEvent: string;
  thirdPartyPayerName: string;
  thirdPartyPayerIdentificationNumber?: string; // HM only as of 2026-08
}

